/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The Account 360 hero: the shared v2 record header (RecordHero) filled with
 * an account's type, status, partner details, edition, owner, industry,
 * place, website, phone and email.
 */

import PropTypes from "prop-types";
import { Building2, Globe, Handshake, Mail, MapPin, Phone, User, UserRound, Briefcase } from "lucide-react";
import RecordHero, { HeroBadge } from "components/v2/record/RecordHero";
import { StatusBadge } from "./RelatedList";
import { linkedValue } from "components/v2/ContactValue";
import { websiteLabel } from "components/v2/contactLinks";
import UserLink from "components/v2/UserLink";

const TYPE_ICONS = { Company: Building2, Partner: Handshake, Individual: User };

export default function Account360Hero({ account, ownerName, ownerId, eyebrow, accent, verticalName, VerticalIcon, extraBadges, actions }) {
  const TypeIcon = TYPE_ICONS[account?.account_type] || Building2;
  const place = [account?.billing_city, account?.billing_state, account?.billing_country].filter(Boolean).join(", ");
  const website = account?.website ? linkedValue("Website", String(account.website), websiteLabel(account.website), { showIcon: false }) : null;
  const phone = account?.phone ? linkedValue("Phone", String(account.phone), account.phone, { showIcon: false }) : null;
  const email = account?.email ? linkedValue("Email", String(account.email), account.email, { showIcon: false }) : null;

  return (
    <RecordHero
      label="Account summary"
      icon={TypeIcon}
      eyebrow={eyebrow}
      title={account?.name}
      accent={accent}
      watermark={VerticalIcon}
      actions={actions}
      badges={
        <>
          {account?.account_type && <HeroBadge>{account.account_type}</HeroBadge>}
          {account?.account_status && <StatusBadge status={account.account_status} />}
          {account?.partner_type && account?.account_type === "Partner" && <HeroBadge>{account.partner_type}</HeroBadge>}
          {account?.partner_code && <HeroBadge>{account.partner_code}</HeroBadge>}
          {verticalName && (
            <HeroBadge accent={accent}>
              {VerticalIcon && <VerticalIcon size={13} aria-hidden="true" />}
              {verticalName}
            </HeroBadge>
          )}
          {extraBadges}
        </>
      }
      meta={[
        { key: "owner", icon: UserRound, content: ownerName ? <>Owned by <UserLink userId={ownerId} name={ownerName} /></> : null },
        { key: "industry", icon: Briefcase, content: account?.industry },
        { key: "place", icon: MapPin, content: place || null },
        { key: "website", icon: Globe, content: website },
        { key: "phone", icon: Phone, content: phone },
        { key: "email", icon: Mail, content: email },
      ]}
    />
  );
}

Account360Hero.propTypes = {
  account: PropTypes.object,
  ownerName: PropTypes.string,
  ownerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  eyebrow: PropTypes.string.isRequired,
  accent: PropTypes.shape({ main: PropTypes.string, ink: PropTypes.string, tint: PropTypes.string }).isRequired,
  verticalName: PropTypes.string,
  VerticalIcon: PropTypes.elementType,
  extraBadges: PropTypes.node,
  actions: PropTypes.node,
};

Account360Hero.defaultProps = { account: null, ownerName: null, ownerId: null, verticalName: null, VerticalIcon: null, extraBadges: null, actions: null };
